import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, ArrowRight, Flag, Compass, Heart, Star,
  CheckCircle, Target, TreePine, Activity, Anchor,
  Repeat, PenTool, Flame, Award, Crosshair
} from 'lucide-react';
import Header from '../components/Header';
import QMGSymbols from '../components/QMGSymbols';
import Footer from '../components/Footer';

const VALUES_EXAMPLES = [
  { value: 'Courage', description: 'Playing the shot you know is right, even when it\'s the harder option.' },
  { value: 'Discipline', description: 'Following your pre-shot routine on every single shot, not just when it\'s comfortable.' },
  { value: 'Resilience', description: 'Competing fully on every hole, regardless of the score.' },
  { value: 'Patience', description: 'Trusting the process and your preparation, especially when results are slow.' },
  { value: 'Integrity', description: 'Being honest in your self-assessment and calling penalties on yourself.' },
  { value: 'Growth', description: 'Treating every round — good or bad — as an opportunity to learn something.' },
];

const SKILLS = [
  {
    icon: Compass,
    title: 'Identifying Your Personal Values',
    description: 'Values are the qualities you want to embody as a golfer and a person. They are your compass when the pressure rises and your mind tries to pull you off course. Unlike goals (which can be achieved or missed), values are a direction you move towards continuously.',
    onCourse: 'Before each round, choose one value to focus on. Write it on your scorecard or glove. When you face a difficult moment, ask: "What would [my value] look like right now?"',
    offCourse: 'Write down your top 5 personal values as a golfer. For each one, describe what it looks like in action on the course. Revisit this list monthly and refine it.',
    inApp: 'Set your values in your QMG profile. Tag diary entries with "Action" when you act in line with your values during a round, especially when it was hard.',
  },
  {
    icon: Crosshair,
    title: 'Pre-Shot Action Routine',
    description: 'Action means stepping into every shot with full intention. A pre-shot routine isn\'t just about alignment and swing thoughts — it\'s about reaching a "go" state where you\'ve made your decision and committed to it completely. Doubt is the enemy of a free swing.',
    onCourse: 'Build a 3-step commitment routine: (1) Decide — pick your shot and commit. (2) Visualise — see and feel the shot. (3) Execute — step in and swing with trust. If doubt creeps in, step back and restart.',
    offCourse: 'Practise your full pre-shot routine on the range. Make the routine itself the goal, not the ball flight. Do 10 shots focusing purely on the quality of your commitment.',
    inApp: 'Rate your "Before the Round" and "During the Round" scores in Performance Scorecards. Action to routine shows up directly in these numbers over time.',
  },
  {
    icon: Flag,
    title: 'Process Goals Over Outcome Goals',
    description: 'Outcome goals ("shoot under 80") create anxiety because they\'re partly outside your control. Process goals ("complete my routine on every shot") are entirely within your control. Action means anchoring your focus on what you can do, not what you hope happens.',
    onCourse: 'Set 2-3 process goals before each round: "I will complete my breathing routine before every shot," "I will accept every outcome within 5 steps," "I will commit to my club selection without second-guessing."',
    offCourse: 'After each round, review your process goals rather than your score. Did you follow through? Rate yourself honestly. Over time, process adherence predicts score improvement.',
    inApp: 'Use Tasks & Goals in your dashboard to set process goals. Log your adherence in session notes. Review trends in your Reports section.',
  },
  {
    icon: Anchor,
    title: 'Values-Driven Decision Making',
    description: 'Under pressure, most golfers default to fear-based decisions — laying up when they should go for it, aiming at the middle of the green when the pin demands courage. Values-driven decisions come from asking "What kind of golfer do I want to be?" rather than "What\'s the safest option?"',
    onCourse: 'When facing a tough decision, pause and ask: "Am I choosing this because of fear, or because it aligns with the player I want to be?" Sometimes the smart play IS the safe play — but make it a choice, not a reaction.',
    offCourse: 'Review your last 5 rounds. Identify 2-3 moments where fear drove your decision. Visualise making the courageous choice instead. How would it have felt?',
    inApp: 'Record key decisions and their motivations in your Diary. Over time, you\'ll see whether you\'re moving towards or away from your values under pressure.',
  },
  {
    icon: Repeat,
    title: 'Recommitment After Setbacks',
    description: 'Every golfer faces setbacks — a triple bogey, a lost ball, a bad stretch of holes. Action isn\'t about never faltering; it\'s about how quickly you recommit. The best competitors in the world don\'t avoid setbacks — they have world-class recovery skills.',
    onCourse: 'After a setback, use a "reset trigger" — adjust your glove, take a deep breath, look at the sky. Then recommit: "The next shot is the most important shot of my round." Mean it.',
    offCourse: 'Practise your reset routine in training. Intentionally hit a bad shot, then go through your full recovery process before the next one. Make the recovery automatic.',
    inApp: 'Your "In-Between Shots" and "After the Round" scorecard scores reflect your recommitment ability. Track improvement over the season.',
  },
];

const MACActionPage = () => {
  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Header />

      <main className="pt-24 pb-20">
        {/* Hero */}
        <section className="bg-[#1A1A1A] py-16 md:py-24">
          <div className="container mx-auto px-6 md:px-12 max-w-4xl">
            <Link to="/mac-research" className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm mb-8 transition-colors" data-testid="back-link">
              <ArrowLeft className="w-4 h-4" /> Back to The Research
            </Link>
            <p className="text-white/40 text-sm uppercase tracking-[0.2em] mb-2">Step Three</p>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-medium text-white mb-4">
              Step In — Action
            </h1>
            <p className="text-white/70 text-base md:text-lg leading-relaxed max-w-2xl">
              Commit fully to your process and personal values. Execute with attention on your swing keys and "feels." This is where awareness and acceptance become action.
            </p>
            <div className="mt-8">
              <QMGSymbols active="action" tone="light" size="md" />
            </div>
          </div>
        </section>

        <div className="container mx-auto px-6 md:px-12 max-w-4xl">

          {/* What It Means */}
          <section className="py-12 md:py-16" data-testid="what-it-means">
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1A] mb-6">What Action Means in Golf</h2>
            <div className="bg-white border border-[#E7E5E4] p-6 md:p-8">
              <p className="text-[#57534E] leading-relaxed mb-4">
                Action is the third skill of the QMG Blueprint. You've noticed your internal state (Awareness). You've accepted it without fighting (Acceptance). Now you <strong>act in service of what matters</strong> — your values, your process, and the shot in front of you.
              </p>
              <p className="text-[#57534E] leading-relaxed mb-4">
                In the MAC research, commitment is rooted in <strong>values-driven action</strong>. This means your behaviour on the course is guided by the golfer you want to be, not by the emotion you happen to be feeling. You can feel nervous AND commit to your routine. You can feel frustrated AND compete on every shot.
              </p>
              <p className="text-[#57534E] leading-relaxed">
                This is the moment where the mental game becomes visible. Action is what the caddie sees, what the coach observes, and what the scorecard reflects. It's the bridge between inner work and outer results.
              </p>
            </div>
          </section>

          {/* Research Quote */}
          <section className="pb-12 md:pb-16" data-testid="research-quote">
            <div className="bg-[#F2F0E9] border-l-4 border-[#1A1A1A] p-6 md:p-8">
              <blockquote className="font-['Playfair_Display'] text-lg md:text-xl text-[#1A1A1A] leading-relaxed mb-4">
                "The correctly applied, manualized MAC protocol is considered a first-line option for performance enhancement with high-level competitive athletes."
              </blockquote>
              <p className="text-[#737373] text-sm">— Gardner & Moore, Clinical Sport Psychology, 2006</p>
            </div>
          </section>

          {/* Personal Values */}
          <section className="pb-12 md:pb-16" data-testid="values-section">
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1A] mb-6">Your Personal Values as a Golfer</h2>
            <div className="bg-white border border-[#E7E5E4] p-6 md:p-8">
              <p className="text-[#57534E] leading-relaxed mb-6">
                Values are not goals. Goals are destinations; values are the compass that guides your journey. You can never "achieve" a value — but you can act in alignment with it every single day. Here are examples of values that golfers find meaningful:
              </p>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                {VALUES_EXAMPLES.map((v, i) => (
                  <div key={i} className="bg-[#F2F0E9] p-4" data-testid={`value-${i}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Star className="w-4 h-4 text-[#BC562B]" />
                      <p className="font-semibold text-[#1A1A1A]">{v.value}</p>
                    </div>
                    <p className="text-sm text-[#57534E]">{v.description}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 bg-[#2C5530]/5 p-4 border-l-3 border-[#2C5530]">
                <p className="text-sm text-[#57534E]">
                  <strong>Your turn:</strong> What are YOUR top 3-5 values as a golfer? Write them down. Put them somewhere you'll see them before every round. When pressure hits, these values become your anchor.
                </p>
              </div>
            </div>
          </section>

          {/* Skills to Train */}
          <section className="pb-12 md:pb-16" data-testid="skills-section">
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#1A1A1A] mb-2">Skills to Train</h2>
            <p className="text-[#57534E] mb-8">Practical commitment skills that turn awareness and acceptance into performance.</p>

            <div className="space-y-8">
              {SKILLS.map((skill, i) => (
                <div key={i} className="bg-white border border-[#E7E5E4] overflow-hidden" data-testid={`skill-${i}`}>
                  <div className="p-6 md:p-8">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 bg-[#1A1A1A] text-white rounded-full flex items-center justify-center flex-shrink-0">
                        <skill.icon className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2">{skill.title}</h3>
                        <p className="text-[#57534E] leading-relaxed">{skill.description}</p>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4 mt-6">
                      <div className="bg-[#2C5530]/5 p-4">
                        <p className="text-xs text-[#2C5530] uppercase tracking-wider font-semibold mb-2 flex items-center gap-1"><Target className="w-3.5 h-3.5" /> On the Course</p>
                        <p className="text-sm text-[#57534E]">{skill.onCourse}</p>
                      </div>
                      <div className="bg-[#BC562B]/5 p-4">
                        <p className="text-xs text-[#BC562B] uppercase tracking-wider font-semibold mb-2 flex items-center gap-1"><TreePine className="w-3.5 h-3.5" /> Off the Course</p>
                        <p className="text-sm text-[#57534E]">{skill.offCourse}</p>
                      </div>
                      <div className="bg-[#1A1A1A]/5 p-4">
                        <p className="text-xs text-[#1A1A1A] uppercase tracking-wider font-semibold mb-2 flex items-center gap-1"><Activity className="w-3.5 h-3.5" /> Track in QMG</p>
                        <p className="text-sm text-[#57534E]">{skill.inApp}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* The Action Test */}
          <section className="pb-12 md:pb-16" data-testid="commitment-test">
            <div className="bg-[#1A1A1A] p-6 md:p-10 text-white">
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl mb-6 text-white">The Action Test</h2>
              <p className="text-white/80 leading-relaxed mb-6">
                Before every shot, ask yourself these three questions. If you can answer "yes" to all three, step in and swing with freedom.
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  { num: '1', question: 'Have I made a clear decision?', detail: 'Club, target, shot shape — decided, not debated.' },
                  { num: '2', question: 'Can I see and feel the shot?', detail: 'A vivid picture of the ball flight and landing.' },
                  { num: '3', question: 'Am I ready to trust it?', detail: 'Willing to accept the outcome, whatever it is.' },
                ].map((q, i) => (
                  <div key={i} className="bg-white/10 p-5">
                    <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center mb-3">
                      <span className="text-lg font-bold">{q.num}</span>
                    </div>
                    <p className="font-semibold text-white mb-1">{q.question}</p>
                    <p className="text-white/60 text-sm">{q.detail}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Navigation */}
          <section className="pb-8" data-testid="nav-section">
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/mac/mindful" className="bg-white border border-[#E7E5E4] p-6 hover:border-[#2C5530]/50 hover:shadow-md transition-all group" data-testid="nav-mindful">
                <p className="text-xs text-[#737373] uppercase tracking-wider mb-1">Step One</p>
                <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2 group-hover:text-[#2C5530] transition-colors">Step Back — Awareness</h3>
                <p className="text-sm text-[#57534E]">Develop moment-to-moment awareness of your internal state.</p>
                <ArrowLeft className="w-5 h-5 text-[#2C5530] mt-3" />
              </Link>
              <Link to="/mac/accepting" className="bg-white border border-[#E7E5E4] p-6 hover:border-[#BC562B]/50 hover:shadow-md transition-all group" data-testid="nav-accepting">
                <p className="text-xs text-[#737373] uppercase tracking-wider mb-1">Step Two</p>
                <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2 group-hover:text-[#BC562B] transition-colors">Step Up — Accepting</h3>
                <p className="text-sm text-[#57534E]">Accept your thoughts and feelings without judgement.</p>
                <ArrowLeft className="w-5 h-5 text-[#BC562B] mt-3" />
              </Link>
            </div>
            <div className="mt-4 text-center">
              <Link to="/mac-research" className="inline-flex items-center gap-2 text-sm text-[#2C5530] font-medium hover:underline" data-testid="nav-research">
                <ArrowLeft className="w-4 h-4" /> Back to The Research & Evidence
              </Link>
            </div>
          </section>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default MACActionPage;
